import { Button, Loading } from '@/components'
import { Layout } from '@/components'
import { DialogPopup } from '@/components/Dialog/DialogPopup'
import Input from '@/components/Form/Input'
import LanguageSelect from '@/components/Form/LanguageSelect'
import Password from '@/components/Form/Password'
import { NotifyPreferredBrowser } from '@/components/molecules/NotifyPreferredBrowser'
import { GeoInOutManager } from '@/components/molecules'
import { Divider, LabelWithLink } from '@/components/ui'
import { availableState } from '@/features/authSlice'
import { dialogRegisterQuestionnaireState } from '@/features/dialogSlice'
import { raundUrl } from '@/lib/custom'
import { useCampaignHeaders } from '@/lib/useCampaignHeaders'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { useFetchGet } from '@/lib/useFetch'
import { useI18nUrls } from '@/lib/useI18nUrls'
import { useIsIOS } from '@/lib/useIsIos'
import { useIsPWA } from '@/lib/useIsPwa'
import { usePaakAd } from '@/lib/usePaakAd'
import { usePreferredBrowser } from '@/lib/usePreferredBrowser'
import { useTracking } from '@/lib/useTracking'
import type { RootState } from '@/store/store'
import type { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'
export { getStaticPaths, getStaticProps } from '@/lib/getStaticFunctions'

type Inputs = {
  username: string
  postalcode: string
  address: string
  password: string
  account: string
}

const Page: NextPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  useTracking({ page: 'signin' })
  const [signinState, setSigninState] = useState<string | undefined>('')
  const [queryUsername, setQueryUsername] = useState<string | undefined>('')
  const { headers } = useCampaignHeaders()
  const { termsUrl, faqUrl, privacyPolicyUrl } = useI18nUrls()
  // const { options } = usePreferredBrowser()
  const { isPWA } = useIsPWA()
  const { isIOS } = useIsIOS()

  const availableStateSelector = useSelector((state: RootState) => state.auth.available)
  const loginTypeStateSelector = useSelector((state: RootState) => state.auth.loginType)
  const { getWithoutAuth: campaignGet } = useFetchGet<{ campaign: Api.CampaignResBody }>({})
  const { data: campaignData } = useSWR<{ campaign: Api.CampaignResBody }>(raundUrl.campaign, campaignGet)

  const { gen, LOCAL_STORAGE_JWT_KEY, LOCAL_STORAGE_USERNAME_KEY, LOCAL_STORAGE_REFRESH_KEY } = useCampaignSlug()
  usePaakAd({ campaign: campaignData?.campaign, requestWithUserId: false })

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_JWT_KEY)) {
      if (loginTypeStateSelector !== 9) {
        router.push({
          pathname: gen('/'),
        })
      }
    }
    return
  }, [loginTypeStateSelector])

  const useFormMethods = useForm<Inputs>()

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormMethods

  useEffect(() => {
    // クエリパラメータからユーザー名を取得して自動入力
    if (campaignData) {
      if (campaignData.campaign.custom === 'NTTBS') {
        setQueryUsername(
          router.query.username && typeof router.query.username === 'string' ? router.query.username : '',
        )
      }
    }
  }, [campaignData])

  const onSubmitDialog: SubmitHandler<Inputs> = async (data) => {
    dispatch(availableState(true))

    try {
      const authAccountFetch = await fetch(raundUrl.login, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          username: data.account,
          password: data.password,
        }),
      })

      const res = await authAccountFetch.json()
      const result = res.result

      if (result.status === 200) {
        localStorage.setItem(LOCAL_STORAGE_JWT_KEY, `${result.id_token}`)
        localStorage.setItem(LOCAL_STORAGE_REFRESH_KEY, `${result.refresh_token}`)
        localStorage.setItem(LOCAL_STORAGE_USERNAME_KEY, `${result.username}`)

        setSigninState('')
        dispatch(availableState(true))
        if (campaignData && campaignData.campaign && campaignData.campaign.custom === 'SOKOSHIGA') {
          if (process.env.NEXT_PUBLIC_SITEURL === 'https://app.stg.raund.net') {
            router.push(gen('/rewards/detail/?query=629'))
          } else if (process.env.NEXT_PUBLIC_SITEURL === 'https://app.raund.net') {
            router.push(gen('/rewards/detail/?query=1445'))
          } else {
            router.push(gen('/rewards/detail/?query=629'))
          }
        } else {
          router.push(gen('/'))
        }
        return
      }

      setSigninState(result.message)
      dispatch(availableState(false))
      return
    } catch (error) {
      setSigninState(t('login.not_registered', 'アカウント名とパスワードが登録されていません。'))
      dispatch(availableState(false))
    }
  }

  return (
    <Layout title="">
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
        {/* NTT西日本の案件のみ */}
        {process.env.NEXT_PUBLIC_API_URL === 'https://api.raund.net' && (
          <>
            {/* Facebook Pixel Code */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1416743506315651');
              fbq('track', 'PageView');
            `,
              }}
            />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src="https://www.facebook.com/tr?id=1416743506315651&ev=PageView&noscript=1"
                alt=""
              />
            </noscript>

            {/* Google tag (gtag.js) */}
            <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17234739756" />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-17234739756');
            `,
              }}
            />

            {/* LINE Tag Base Code */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
              (function(g,d,o){
                g._ltq=g._ltq||[];g._lt=g._lt||function(){g._ltq.push(arguments)};
                var h=location.protocol==='https:'?'https://d.line-scdn.net':'http://d.line-cdn.net';
                var s=d.createElement('script');s.async=1;
                s.src=o||h+'/n/line_tag/public/release/v1/lt.js';
                var t=d.getElementsByTagName('script')[0];t.parentNode.insertBefore(s,t);
                  })(window, document);
              _lt('init', {
                customerType: 'lap',
                tagId: 'a7659774-7d9a-4908-b04d-8b12efdafff9'
              });
              _lt('send', 'pv', ['a7659774-7d9a-4908-b04d-8b12efdafff9']);
            `,
              }}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              _lt('send', 'cv', {
                type: 'Raund'
              },['a7659774-7d9a-4908-b04d-8b12efdafff9']);
            `,
              }}
            />
          </>
        )}
      </Head>

      <section className="w-full">
        <img src={campaignData?.campaign?.image} className="w-full" alt="" />
        <div className="mx-2 mt-10">
          {!campaignData?.campaign.registration && campaignData?.campaign.custom === 'NTT' && (
            <div className={'px-4'}>
              <div className="w-full h-50 border-4 border-red-400 mb-4">
                <p className={'p-2 text-md text-[16px] text-red-500'}>
                  特典が全て配布されましたのでキャンペーンを終了いたします。
                </p>
                <p className={'p-2 text-md text-[16px] text-red-500'}>特典を獲得している方のみログイン可能です。</p>
              </div>
            </div>
          )}
          {(campaignData?.campaign.registration || campaignData?.campaign.custom === 'NTT') && (
            <>
              <Divider
                text={
                  campaignData?.campaign?.isLineLogin && !(isIOS && isPWA)
                    ? t('login.register_with_line')
                    : t('login.register')
                }
              />
              {campaignData?.campaign?.isLineLogin && !(isIOS && isPWA) && (
                <div className="text-center">
                  <a
                    href={`${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/oauth2/authorize?identity_provider=${campaignData?.campaign.cognitoIdentityProvider}&redirect_uri=${campaignData?.campaign.url}/auth/check/&response_type=CODE&client_id=${campaignData?.campaign.cognitoClientId}&scope=email openid profile`}
                    className="mx-auto mb-[16px] block w-[71.7%]"
                  >
                    <img src={`${process.env.NEXT_PUBLIC_SITEURL}/images/btn_line.png`} alt="" />
                  </a>
                </div>
              )}
              {/* 4はLINEのみ */}
              {loginTypeStateSelector !== 4 && (
                <>
                  {campaignData?.campaign.registration && (
                    <div className="mx-auto w-11/12 px-6">
                      {loginTypeStateSelector === 9 ? (
                        <Button
                          text={t('login.for_new_user')}
                          borderColor="border-main"
                          bgColor="bg-[#fff]"
                          onClick={() => router.reload()}
                        />
                      ) : (
                        <Button
                          text={t('login.for_new_user')}
                          borderColor="border-main"
                          bgColor="bg-[#fff]"
                          href={gen('/auth/register/')}
                        />
                      )}
                    </div>
                  )}

                  {loginTypeStateSelector !== 9 && (
                    <Divider
                      text={
                        loginTypeStateSelector === 1
                          ? t('login.or_already_with_account_name', 'またはアカウント名でログイン')
                          : t('login.or_already_with_email', 'またはメールアドレスでログイン')
                      }
                      mt={'mt-[24px]'}
                    />
                  )}
                </>
              )}
            </>
          )}

          <div
            className={`mx-auto mt-5 w-11/12 rounded-lg to-white px-6 py-7 ${
              loginTypeStateSelector === 4 ? '' : 'shadow-primary'
            }`}
          >
            {loginTypeStateSelector !== 4 && loginTypeStateSelector !== 9 && (
              <>
                {signinState !== '' ? <div className="questerror text-sm">{`${signinState}`}</div> : <></>}
                <FormProvider {...useFormMethods}>
                  <form className="mb-6" method="" onSubmit={handleSubmit(onSubmitDialog)}>
                    <fieldset>
                      {loginTypeStateSelector === 1 ? (
                        <div className="relative">
                          <Input
                            labelName={t('login.account_name')}
                            htmlfor="account"
                            type="text"
                            placeholder=""
                            register={register}
                            required
                            require={true}
                            ariaLabel="アカウント名"
                            defaultValue={queryUsername}
                          />
                          <p className="text-xs text-color01">{t('login.account_name_notification')}</p>
                          {errors.account?.type === 'required' && (
                            <p className="error text-xss text-color01">
                              {t('login.account_name_error_message_input_required', '※入力してください')}
                            </p>
                          )}
                        </div>
                      ) : (
                        ''
                      )}

                      {loginTypeStateSelector === 2 ? (
                        <div className="relative">
                          <Input
                            labelName={t('login.email')}
                            htmlfor="account"
                            type="text"
                            placeholder=""
                            register={register}
                            validate={{
                              m: (v) =>
                                (v &&
                                  /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/.test(v)) ||
                                t('login.email_error_message_format_error'),
                            }}
                            required
                            require={true}
                            ariaLabel="メールアドレス"
                            defaultValue={queryUsername}
                          />
                          {errors.account?.type === 'm' && (
                            <p className="error text-xs text-color01">{errors.account?.message}</p>
                          )}
                          {errors.account?.type === 'required' && (
                            <p className="error text-xss text-color01">
                              {t('login.email_name_error_message_input_required')}
                            </p>
                          )}
                        </div>
                      ) : (
                        ''
                      )}

                      <div className="mt-6">
                        <Password
                          minLength={campaignData?.campaign.passwordCharacters}
                          validate={{
                            p: (v) => (v && /[0-9]/.test(v)) || t('login.password_error_message_number_required'),
                            e: (v) => (v && /[a-z]/.test(v)) || t('login.password_error_message_alphabet_required'),
                          }}
                        />
                      </div>
                    </fieldset>

                    <Button text={t('login.login')} type="submit" classes="mt-10" />
                  </form>
                </FormProvider>
              </>
            )}

            <LabelWithLink href={termsUrl} text={t('login.terms')} />
            <LabelWithLink href={privacyPolicyUrl} text={t('login.privacy_policy')} mt />
            <LabelWithLink href={faqUrl} text={t('common.faq')} mt />
          </div>
          {campaignData?.campaign &&
            campaignData?.campaign.isLocalize &&
            campaignData?.campaign.languages.length > 1 && (
              <div className="mx-auto mt-[24px] w-11/12">
                <LanguageSelect languages={campaignData?.campaign.languages} />
              </div>
            )}
        </div>
      </section>

      {availableStateSelector ? <Loading /> : ''}

      <DialogPopup hookpoint="LOGIN" campaign={campaignData?.campaign} />
    </Layout>
  )
}

export default Page
